import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Empty, ErrorBanner, TableSkeleton, Badge, Stat, StatGroup, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * What recordings are costing in storage, and whether storage is set up at all.
 *
 * Counted from our own rows: the node reports the size S3 confirmed for each upload (0094), so this
 * answers "how many files, how much space" per account and per month without listing a bucket.
 *
 * The number that matters most is not the total, it is what nothing will ever delete: a recording
 * on an account with no retention set is kept for ever, and that is the figure that grows quietly
 * until somebody reads an invoice.
 */

interface Usage {
  files: number; bytes: number; unsized: number;
  oldest: string | null; newest: string | null;
}
interface Payload {
  configured: boolean;
  bucket: string | null;
  region: string | null;
  lifecycle: 'installed' | 'missing' | 'unknown';
  keys_from: 'portal' | 'environment' | null;
  total: Usage;
  kept_for_ever: { files: number; bytes: number };
  accounts: Array<Usage & { tenant_id: string; account: string }>;
  months: Array<{ month: string; files: number; bytes: number }>;
}

/** Bytes as somebody says them: 2.4 GB, not 2,576,980,378. */
export function size(bytes: number): string {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const n = bytes / 1024 ** i;
  return `${n >= 100 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

export function RecordingStoragePage() {
  const q = useQuery({
    queryKey: ['platform-recording-usage'],
    queryFn: () => api.get<Payload>('/v1/platform/recording-usage'),
    refetchInterval: 60_000,
  });

  if (q.error) return <ErrorBanner error={q.error} />;

  const d = q.data;
  const peak = Math.max(1, ...(d?.months ?? []).map((m) => m.bytes));

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Recording storage</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What is held, what it takes up, and what retention will never remove.
          </p>
        </div>
      </div>

      {d && !d.configured && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            Storage is not configured, so recordings stay on the node's disk. Add the bucket and
            its keys under Provider keys; the node still needs its own uploader key installed with
            {' '}<span className="arc-mono">tools/configure-recordings.mjs</span>.
          </span>
        </div>
      )}

      {d?.configured && d.lifecycle === 'missing' && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            The bucket has no retention rules, so nothing is being deleted whatever an account sets.
            Install them with <span className="arc-mono">configure-recordings.mjs --lifecycle</span>.
          </span>
        </div>
      )}

      {d && (
        <div style={{ marginBottom: 14 }}>
          <StatGroup>
            <Stat bare icon="media" hue={1} label="Recordings held" value={d.total.files.toLocaleString('en-ZA')}
              sub={d.total.oldest ? `oldest ${d.total.oldest}` : 'none yet'} />
            <Stat bare icon="server" hue={2} label="Space used" value={size(Number(d.total.bytes))}
              sub={d.total.unsized > 0 ? `${d.total.unsized} without a size` : 'measured on upload'} />
            <Stat bare icon="clock" hue={3} label="Kept for ever"
              value={Number(d.kept_for_ever.files).toLocaleString('en-ZA')}
              tone={d.kept_for_ever.files > 0 ? 'warn' : undefined}
              sub={`${size(Number(d.kept_for_ever.bytes))}, no retention set`} />
            <Stat bare icon="server" hue={4} label="Bucket"
              value={d.bucket ?? 'not set'}
              sub={d.configured
                ? `${d.region} · lifecycle ${d.lifecycle} · keys from ${d.keys_from}`
                : 'recordings stay on the node'} />
          </StatGroup>
        </div>
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="By month" sub="what was recorded, and is still held" />
        <div className="arc-card-body">
          {q.isLoading ? <div className="arc-skeleton" style={{ height: 90 }} />
            : (d?.months.length ?? 0) === 0 ? (
              <Empty icon="media" title="Nothing held yet" />
            ) : (
              <div className="arc-stack" style={{ gap: 6 }}>
                {d!.months.map((m) => (
                  <div key={m.month} className="arc-row" style={{ gap: 10, alignItems: 'center' }}>
                    <span className="arc-mono arc-subtle" style={{ width: 62, fontSize: 12 }}>{m.month}</span>
                    <span style={{
                      height: 10, borderRadius: 3, background: 'var(--arc-accent-quiet)',
                      border: '1px solid var(--arc-accent-border)',
                      width: `${Math.max(2, (Number(m.bytes) / peak) * 100)}%`,
                    }} />
                    <span className="arc-subtle" style={{ fontSize: 12 }}>
                      {size(Number(m.bytes))} · {m.files.toLocaleString('en-ZA')} calls
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      <div className="arc-card">
        <CardHead title="By account" sub="largest first" />
        {q.data === undefined ? <TableSkeleton rows={4} cols={5} />
          : (d?.accounts.length ?? 0) === 0 ? (
            <Empty icon="media" title="No recordings held yet"
              hint="Each account appears here once it has one. Recording is switched on per extension, under an account's Call recording." />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className="arc-td-num" style={{ width: 120 }}>Recordings</th>
                    <th className="arc-td-num" style={{ width: 120 }}>Space</th>
                    <th style={{ width: 130 }}>Oldest</th>
                    <th style={{ width: 130 }}>Newest</th>
                  </tr>
                </thead>
                <tbody>
                  {d!.accounts.map((a) => (
                    <tr key={a.tenant_id}>
                      <td style={{ fontWeight: 550 }}>{a.account}</td>
                      <td className="arc-td-num">{a.files.toLocaleString('en-ZA')}</td>
                      <td className="arc-td-num">
                        {size(Number(a.bytes))}
                        {a.unsized > 0 && (
                          <span className="arc-subtle" style={{ fontSize: 11 }}> +{a.unsized}</span>
                        )}
                      </td>
                      <td className="arc-muted arc-mono" style={{ fontSize: 12 }}>{a.oldest ?? ''}</td>
                      <td className="arc-muted arc-mono" style={{ fontSize: 12 }}>{a.newest ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {(d?.total.unsized ?? 0) > 0 && (
        <p className="arc-subtle" style={{ fontSize: 12, marginTop: 10 }}>
          {d!.total.unsized} recordings were uploaded before sizes were kept, so the space shown is
          a floor rather than the whole of it.
        </p>
      )}
    </>
  );
}
